import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, X, Coffee } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import useSeo from "../hooks/useSeo";

/**
 * VsPage — reusable comparison page component used by /vs/stock-alarm,
 * /vs/mindful-trader, /vs/finviz. Audit D5.3 called out zero /vs pages
 * in a category where competitor-name searches are high-intent — this
 * plants three targeted pages, each with a feature table and a
 * "when to choose each" section.
 */
export default function VsPage({ slug, competitor, tagline, competitorPrice, mbPrice = "$14.99/mo", rows, whenUs, whenThem }) {
  useSeo({
    title: `Moonbucks vs ${competitor} — which alert service should you pick?`,
    description: `${competitor} vs Moonbucks — pricing, features, track record transparency, and the honest "when to choose each" call.`,
    canonical: `https://moonbucks.io/vs/${slug}`,
  });

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline mb-6" data-testid="vs-back-link">
          <ArrowLeft className="w-3 h-3" /> Back to home
        </Link>

        <div className="label-uppercase text-mb-emerald mb-3">Comparison</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight" data-testid={`vs-${slug}-title`}>
          Moonbucks vs {competitor}
        </h1>
        <p className="mt-4 text-neutral-600 text-lg max-w-2xl leading-relaxed">{tagline}</p>

        {/* Price bar */}
        <div className="mt-8 grid grid-cols-2 gap-4">
          <div className="bg-white border border-mb-border rounded-lg p-5">
            <div className="label-uppercase text-neutral-500 text-[10px]">{competitor}</div>
            <div className="font-mono-data text-3xl font-extrabold mt-1">{competitorPrice}</div>
          </div>
          <div className="bg-mb-emerald/5 border-2 border-mb-emerald rounded-lg p-5">
            <div className="label-uppercase text-mb-emerald text-[10px]">Moonbucks</div>
            <div className="font-mono-data text-3xl font-extrabold text-mb-emerald mt-1">{mbPrice}</div>
            <div className="text-[11px] text-mb-emerald/70 mt-0.5">or $4.99/week · cancel anytime</div>
          </div>
        </div>

        {/* Feature table */}
        <div className="mt-8 bg-white border border-mb-border rounded-lg overflow-hidden" data-testid={`vs-${slug}-table`}>
          <table className="w-full text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-left px-4 py-3 label-uppercase text-[10px] text-neutral-500">Feature</th>
                <th className="text-center px-4 py-3 label-uppercase text-[10px] text-neutral-500">{competitor}</th>
                <th className="text-center px-4 py-3 label-uppercase text-[10px] text-mb-emerald">Moonbucks</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.label} className={i > 0 ? "border-t border-mb-border/50" : ""}>
                  <td className="px-4 py-3 text-neutral-700">{r.label}</td>
                  <td className="px-4 py-3 text-center">{cell(r.them)}</td>
                  <td className="px-4 py-3 text-center bg-mb-emerald/5">{cell(r.us)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* When to choose each */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-mb-border rounded-lg p-6">
            <div className="label-uppercase text-neutral-500">When {competitor} makes sense</div>
            <ul className="mt-3 space-y-2 text-sm text-neutral-700">
              {whenThem.map((w) => (
                <li key={w} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-neutral-400 mt-2 flex-shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-mb-emerald/5 border-2 border-mb-emerald rounded-lg p-6">
            <div className="label-uppercase text-mb-emerald">When Moonbucks makes sense</div>
            <ul className="mt-3 space-y-2 text-sm text-neutral-800">
              {whenUs.map((w) => (
                <li key={w} className="flex items-start gap-2">
                  <Coffee className="w-4 h-4 text-mb-emerald mt-0.5 flex-shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white border border-mb-border rounded-lg p-6">
          <div>
            <div className="font-display font-semibold" data-testid={`vs-${slug}-closing-line`}>Try Moonbucks for a week — $4.99. Cancel anytime.</div>
          </div>
          <Link to="/register?plan=weekly">
            <Button className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md" data-testid={`vs-${slug}-cta`}>
              Start brewing alerts <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <p className="mt-6 text-[11px] text-neutral-400 italic">
          Pricing and features accurate as of publication. Competitor names and pricing referenced under fair-use nominative comparison. Moonbucks is educational — not investment advice.
        </p>
      </div>
    </div>
  );
}

function cell(v) {
  if (v === true) return <Check className="w-4 h-4 text-mb-emerald mx-auto" />;
  if (v === false) return <X className="w-4 h-4 text-neutral-400 mx-auto" />;
  return <span className="text-neutral-700">{v}</span>;
}
